import { FC, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { PostLoginHandlers, AppOIDCConfig, IdpType } from './types';

interface AppOktaLoginWidgetProps extends PostLoginHandlers {
  oktaType: IdpType;
  ascendSsarOIDCConfig: AppOIDCConfig;
}

const AppOktaLoginWidget: FC<AppOktaLoginWidgetProps> = (props) => {
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'redirecting'>('checking');

  const { issuer, scopes } =
    props.oktaType === IdpType.OktaGDC
      ? props.ascendSsarOIDCConfig.b2bOidc
      : props.oktaType === IdpType.OktaB2E
      ? props.ascendSsarOIDCConfig.b2eOidc
      : props.ascendSsarOIDCConfig.b2bOidc;

  useEffect(() => {
    // Already authenticated (e.g. page refresh with valid tokens)
    if (authState?.isAuthenticated) {
      setStatus('authenticated');
      return;
    }

    const trySilentAuth = async () => {
      try {
        // Step 1: Check local token storage first
        const existing = await oktaAuth.tokenManager.getTokens();
        if (existing?.accessToken && !oktaAuth.tokenManager.hasExpired(existing.accessToken)) {
          console.log('SSO: Valid tokens found in storage');
          setStatus('authenticated');
          return;
        }

        // Step 2: Silent auth via hidden iframe — reuses Okta session cookie from dashboard login
        console.log('SSO: Attempting silent auth via Okta session...');
        const { tokens } = await oktaAuth.token.getWithoutPrompt({
          issuer,
          scopes,
        });
        await oktaAuth.tokenManager.setTokens(tokens);
        console.log('SSO: Silent auth succeeded — session reused');
        setStatus('authenticated');
      } catch (err: any) {
        // No active Okta session — redirect to Okta hosted login page
        console.log('SSO: No session found, redirecting to Okta login:', err.errorCode || err.message);
        setStatus('redirecting');
        oktaAuth.signInWithRedirect({ originalUri: '/home' });
      }
    };

    trySilentAuth();
  }, [authState?.isAuthenticated, oktaAuth, issuer, scopes]);

  // Navigate to /home once authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      navigate('/home');
    }
  }, [status, navigate]);

  return (
    <div style={{ padding: '30px', textAlign: 'center' }}>
      {status === 'checking' && (
        <>
          <p style={{ color: '#666', marginBottom: '12px' }}>Signing you in...</p>
          <div style={{
            width: '28px', height: '28px',
            border: '3px solid #f0f0f0',
            borderTop: '3px solid #af1685',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </>
      )}
      {status === 'redirecting' && (
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      )}
      {status === 'authenticated' && (
        <p style={{ color: '#4caf50' }}>✅ Authenticated! Redirecting...</p>
      )}
    </div>
  );
};

export default AppOktaLoginWidget;